/**
 * useActiveSpeurtocht Hook
 * Fetches active speurtocht session for the current user's team
 * Used for dashboard cards and quick access
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { ActiveSessionData } from "../types";

interface UseActiveSpeurttochtResult {
  /** Active session data, null if no active session */
  activeSession: ActiveSessionData | null;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** Refetch the active session */
  refresh: () => Promise<void>;
}

/**
 * Hook to fetch active speurtocht session for the user's team
 */
export function useActiveSpeurtocht(): UseActiveSpeurttochtResult {
  const { user, activeWeekend, userTeam } = useAuth();
  const [activeSession, setActiveSession] = useState<ActiveSessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch active session
   */
  const fetchActiveSession = useCallback(async () => {
    if (!user?.id || !activeWeekend?.id || !userTeam?.id) {
      setActiveSession(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Query for active sessions for the user's team
      const { data, error: queryError } = await supabase
        .from("speurtocht_sessions")
        .select(`
          id,
          speurtocht_id,
          current_step_index,
          total_steps,
          completed_steps,
          started_at,
          expires_at,
          total_points,
          speurtocht:speurtochts (
            name,
            icon,
            color
          )
        `)
        .eq("team_id", userTeam.id)
        .eq("status", "active")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (queryError) throw queryError;

      if (!data || !data.speurtocht) {
        setActiveSession(null);
        return;
      }

      // Map to ActiveSessionData
      const sessionData: ActiveSessionData = {
        session_id: data.id,
        speurtocht_id: data.speurtocht_id,
        speurtocht_name: (data.speurtocht as any).name,
        speurtocht_icon: (data.speurtocht as any).icon,
        speurtocht_color: (data.speurtocht as any).color,
        current_step_index: data.current_step_index,
        total_steps: data.total_steps,
        completed_steps: data.completed_steps,
        started_at: data.started_at,
        expires_at: data.expires_at,
        total_points: data.total_points,
      };

      setActiveSession(sessionData);
    } catch (err) {
      console.error("Error fetching active speurtocht session:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch active session");
      setActiveSession(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, activeWeekend?.id, userTeam?.id]);

  // Initial fetch
  useEffect(() => {
    fetchActiveSession();
  }, [fetchActiveSession]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!userTeam?.id) return;

    const subscription = supabase
      .channel("active-speurtocht-session")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "speurtocht_sessions",
          filter: `team_id=eq.${userTeam.id}`,
        },
        () => {
          // Refetch on any change to the team's sessions
          fetchActiveSession();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userTeam?.id, fetchActiveSession]);

  return {
    activeSession,
    loading,
    error,
    refresh: fetchActiveSession,
  };
}

export default useActiveSpeurtocht;

